import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { classNames, useTranslate } from "helpers";
import { useNotificationHub } from "services/signalr/useNotificationHub";
import { NotificationInboxItem } from "services/api/notifications/types";
import { groupInboxByDay } from "ui/modules/notifications/inboxGrouping";
import { getNotificationRoute } from "ui/modules/notifications/notificationRoute";
// Izravno iz `routes`, ne iz `PageTitle/consts` — taj vuče navigacijske barrele, a s njima
// i stranice, pa se init krug zatvori natrag na ovu komponentu.
import { Routes as NotificationRoutes } from "ui/modules/notifications/navigation/routes";
import { NotificationInboxItemRow } from "./NotificationInboxItemRow";

/**
 * Zvono s obavijestima u zaglavlju. Drži i vlastito otvaranje/zatvaranje — izvan njega
 * nitko ne treba znati za inbox, pa je cijeli iz `Menu` premješten ovamo.
 */
const NotificationInbox: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  // Klik na obavijest preglednika vodi na entitet, isto kao klik u popisu.
  const handleBrowserNotificationClick = useCallback(
    (item: NotificationInboxItem) => {
      const route = getNotificationRoute(item.entityType, item.entityId, item.parentEntityId);

      if (route) navigate(route);
    },
    [navigate]
  );

  const { inboxItems, unreadCount, markAsRead, markAllAsRead, permission, requestPermission } =
    useNotificationHub(handleBrowserNotificationClick);

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Skupine se računaju na trenutak otvaranja; popis je kratak pa to ništa ne košta.
  const groups = useMemo(() => groupInboxByDay(inboxItems, new Date()), [inboxItems]);

  const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const handleItemClick = useCallback(
    (id: string) => {
      markAsRead(id);

      const item = inboxItems.find((i) => i.id === id);
      const route = item
        ? getNotificationRoute(item.entityType, item.entityId, item.parentEntityId)
        : null;

      // Obavijesti bez entiteta (prilagođene) samo se označe pročitanima — panel ostaje
      // otvoren da korisnik može proći kroz ostale.
      if (!route) return;

      setIsOpen(false);
      navigate(route);
    },
    [inboxItems, markAsRead, navigate]
  );

  const handleShowAll = useCallback(() => {
    setIsOpen(false);
    navigate(NotificationRoutes.NotificationLogSearchPageRouter);
  }, [navigate]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="relative rounded-full p-1 text-[#9b9daa] hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
      >
        <span className="sr-only">{t("admin.notification.inbox.title")}</span>
        <svg
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-[16px] rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 z-20 mt-2 w-80 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <span className="text-sm font-semibold text-gray-800">
              {t("admin.notification.inbox.title")}
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                {t("admin.notification.inbox.markAllRead")}
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {groups.length === 0 ? (
              <div className="px-4 py-8 flex flex-col items-center text-center">
                <svg
                  className="h-8 w-8 text-slate-300 mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
                  />
                </svg>
                <span className="text-sm text-gray-500">
                  {t("admin.notification.inbox.empty")}
                </span>
              </div>
            ) : (
              groups.map((group) => (
                <div key={group.key}>
                  <div
                    className={classNames(
                      "px-4 py-1.5 text-[11px] uppercase tracking-wider font-semibold",
                      "text-slate-400 bg-slate-50 border-b border-slate-100 sticky top-0"
                    )}
                  >
                    {group.label}
                  </div>
                  {group.items.map((item) => (
                    <NotificationInboxItemRow
                      key={item.id}
                      item={item}
                      onClick={handleItemClick}
                    />
                  ))}
                </div>
              ))
            )}
          </div>

          {/* Dozvola se traži tek kad korisnik sam otvori zvono i klikne — traženje pri
              učitavanju stranice preglednici prigušuju, a i djeluje nametljivo. */}
          {permission === "default" && (
            <button
              type="button"
              onClick={requestPermission}
              className="w-full px-4 py-2.5 text-xs text-slate-600 hover:bg-slate-50 border-t text-left"
            >
              Uključi obavijesti i kad Orytron nije otvoren
            </button>
          )}

          <button
            type="button"
            onClick={handleShowAll}
            className="w-full px-4 py-2.5 text-xs font-medium text-blue-600 hover:bg-slate-50 border-t"
          >
            Prikaži sve
          </button>
        </div>
      )}
    </div>
  );
};

export default memo(NotificationInbox);
